/**
 * FIKO PERFORMANCE INTEGRATION
 * ============================
 * Tum performans modullerini tek noktadan yoneten entegrasyon katmani
 *
 * Bu dosya:
 * - Tum FAZA 3-4 modullerini initialize eder
 * - Render loop'a entegre eder
 * - Otomatik fallback ve hata yonetimi saglar
 * - Performance metrikleri toplar
 */

import * as THREE from 'three';

// FAZA 3.1: GPU Adapter
import {
    FikoGPUAdapter,
    isWebGPUAvailable,
    type GPUAdapterStats
} from './FikoGPUAdapter';

// FAZA 3.3: Shared Memory
import {
    FikoSharedMemory,
    isSharedArrayBufferAvailable,
    type SharedMemoryStats
} from './FikoSharedMemory';

// FAZA 4.1: HiZ Occlusion
import {
    FikoHiZOcclusionCulling,
    type HiZConfig,
    type HiZStats
} from './FikoHiZOcclusionCulling';

// FAZA 4.2: View-Dependent LOD
import {
    FikoViewDependentLOD,
    type ViewDependentConfig,
    type ViewDependentStats
} from './FikoViewDependentLOD';

// FAZA 4.3: Advanced Edge Detection
import {
    FikoAdvancedEdgeDetection,
    type EdgeDetectionConfig,
    type EdgeDetectionStats
} from './FikoAdvancedEdgeDetection';

// FAZA 4.4: Memory Pooling
import {
    FikoMemoryPoolManager,
    getMemoryPoolManager,
    memoryPool,
    type PoolStats
} from './FikoMemoryPooling';

// FAZA 5: Dynamic Batching
import { FikoDynamicBatching } from './FikoDynamicBatching';

// ============================================================================
// INTERFACES
// ============================================================================

export interface PerformanceIntegrationConfig {
    /** Enable HiZ occlusion culling */
    enableHiZOcclusion: boolean;
    /** Enable view-dependent LOD */
    enableViewDependentLOD: boolean;
    /** Enable advanced edge detection */
    enableAdvancedEdges: boolean;
    /** Enable memory pooling */
    enableMemoryPooling: boolean;
    /** Enable dynamic batching (draw call reduction) */
    enableDynamicBatching: boolean;
    /** Enable WebGPU (with WebGL2 fallback) */
    preferWebGPU: boolean;
    /** Enable SharedArrayBuffer for workers */
    enableSharedMemory: boolean;
    /** Auto-initialize on construction */
    autoInit: boolean;
    /** Debug mode - extra logging */
    debug: boolean;
}

export interface PerformanceIntegrationStats {
    initialized: boolean;
    initTimeMs: number;
    frameTimeMs: number;
    gpuBackend: 'webgpu' | 'webgl2' | 'none';
    sharedMemoryEnabled: boolean;
    modules: {
        hiZOcclusion: HiZStats | null;
        viewDependentLOD: ViewDependentStats | null;
        edgeDetection: EdgeDetectionStats | null;
        memoryPool: {
            vector3: PoolStats;
            matrix4: PoolStats;
            totalMemoryBytes: number;
        } | null;
    };
}

// ============================================================================
// MAIN CLASS
// ============================================================================

export class FikoPerformanceIntegration {
    private config: PerformanceIntegrationConfig;
    private camera: THREE.Camera;
    private renderer: THREE.WebGLRenderer;
    private scene: THREE.Scene;

    // Modules
    private gpuAdapter: FikoGPUAdapter | null = null;
    private sharedMemory: FikoSharedMemory | null = null;
    private hiZOcclusion: FikoHiZOcclusionCulling | null = null;
    private viewDependentLOD: FikoViewDependentLOD | null = null;
    private edgeDetection: FikoAdvancedEdgeDetection | null = null;
    private memoryPoolManager: FikoMemoryPoolManager | null = null;
    private dynamicBatching: FikoDynamicBatching | null = null;

    // State
    private initialized: boolean = false;
    private initPromise: Promise<void> | null = null;
    private initTimeMs: number = 0;

    // Frame timing
    private lastFrameTime: number = 0;
    private frameTimeAccum: number = 0;
    private frameCount: number = 0;
    private avgFrameTimeMs: number = 0;

    // Registered meshes for LOD and edge detection
    private registeredMeshes: Map<string, THREE.Mesh> = new Map();

    constructor(
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        scene: THREE.Scene,
        config?: Partial<PerformanceIntegrationConfig>
    ) {
        this.camera = camera;
        this.renderer = renderer;
        this.scene = scene;

        this.config = {
            enableHiZOcclusion: config?.enableHiZOcclusion ?? true,
            enableViewDependentLOD: config?.enableViewDependentLOD ?? true,
            enableAdvancedEdges: config?.enableAdvancedEdges ?? true,
            enableMemoryPooling: config?.enableMemoryPooling ?? true,
            enableDynamicBatching: config?.enableDynamicBatching ?? true,
            preferWebGPU: config?.preferWebGPU ?? true,
            enableSharedMemory: config?.enableSharedMemory ?? true,
            autoInit: config?.autoInit ?? true,
            debug: config?.debug ?? false
        };

        if (this.config.autoInit) {
            this.init();
        }
    }

    // ========================================================================
    // INITIALIZATION
    // ========================================================================

    /**
     * Initialize all performance modules
     */
    async init(): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this._initInternal();
        return this.initPromise;
    }

    private async _initInternal(): Promise<void> {
        const startTime = performance.now();

        if (this.config.debug) {
            console.log('[FikoPerformance] Initializing...');
        }

        try {
            // 1. Initialize Memory Pool first (used by other modules)
            if (this.config.enableMemoryPooling) {
                this.memoryPoolManager = getMemoryPoolManager();
                // Warmup: acquire+release to ensure pools are hot
                const warmVecs: THREE.Vector3[] = [];
                for (let i = 0; i < 32; i++) warmVecs.push(this.memoryPoolManager.vector3.acquire());
                for (const v of warmVecs) this.memoryPoolManager.vector3.release(v);
                const warmMats: THREE.Matrix4[] = [];
                for (let i = 0; i < 16; i++) warmMats.push(this.memoryPoolManager.matrix4.acquire());
                for (const m of warmMats) this.memoryPoolManager.matrix4.release(m);
                if (this.config.debug) {
                    console.log('[FikoPerformance] Memory pooling enabled (warmed up)');
                }
            }

            // 1b. Dynamic Batching (draw call reduction)
            if (this.config.enableDynamicBatching) {
                this.dynamicBatching = new FikoDynamicBatching(this.scene);
                if (this.config.debug) {
                    console.log('[FikoPerformance] Dynamic Batching enabled');
                }
            }

            // 2. Initialize GPU Adapter
            if (this.config.preferWebGPU) {
                this.gpuAdapter = FikoGPUAdapter.getInstance({
                    preferWebGPU: true,
                    enableProfiling: this.config.debug
                });
                await this.gpuAdapter.init();
                if (this.config.debug) {
                    console.log(`[FikoPerformance] GPU backend: ${this.gpuAdapter.isWebGPU ? 'WebGPU' : 'WebGL2'}`);
                }
            }

            // 3. Initialize Shared Memory
            // TODO: Activate when worker data transfer is implemented
            // PERF: Disabled - initialized but never used, adds overhead
            // if (this.config.enableSharedMemory && isSharedArrayBufferAvailable()) {
            //     this.sharedMemory = FikoSharedMemory.getInstance();
            //     if (this.config.debug) {
            //         console.log('[FikoPerformance] SharedArrayBuffer enabled');
            //     }
            // }

            // 4. Initialize HiZ Occlusion
            if (this.config.enableHiZOcclusion) {
                this.hiZOcclusion = new FikoHiZOcclusionCulling(
                    this.camera,
                    this.renderer,
                    {
                        pyramidSize: 512,
                        mipLevels: 9,
                        preferWebGPU: this.config.preferWebGPU
                    }
                );
                if (this.config.debug) {
                    console.log('[FikoPerformance] HiZ Occlusion enabled');
                }
            }

            // 5. Initialize View-Dependent LOD
            if (this.config.enableViewDependentLOD) {
                this.viewDependentLOD = new FikoViewDependentLOD(
                    this.camera,
                    this.renderer,
                    this.scene,
                    {
                        maxMemoryBytes: 512 * 1024 * 1024,
                        enableGeomorphing: true,
                        networkAware: true
                    }
                );
                if (this.config.debug) {
                    console.log('[FikoPerformance] View-Dependent LOD enabled');
                }
            }

            // 6. Initialize Advanced Edge Detection
            if (this.config.enableAdvancedEdges) {
                this.edgeDetection = new FikoAdvancedEdgeDetection(
                    this.camera,
                    this.renderer,
                    this.scene,
                    {
                        detectSilhouettes: true,
                        detectCreases: true,
                        creaseAngleThreshold: 30,
                        antialiased: true
                    }
                );
                if (this.config.debug) {
                    console.log('[FikoPerformance] Advanced Edge Detection enabled');
                }
            }

            this.initialized = true;
            this.initTimeMs = performance.now() - startTime;

            if (this.config.debug) {
                console.log(`[FikoPerformance] Initialized in ${this.initTimeMs.toFixed(2)}ms`);
            }

        } catch (error) {
            console.error('[FikoPerformance] Initialization failed:', error);
            // Continue with partial initialization
            this.initialized = true;
        }
    }

    /**
     * Check if fully initialized
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Wait for initialization
     */
    async waitForInit(): Promise<void> {
        if (this.initPromise) {
            await this.initPromise;
        }
    }

    // ========================================================================
    // MESH REGISTRATION
    // ========================================================================

    /**
     * Register a mesh for performance optimization
     */
    registerMesh(id: string, mesh: THREE.Mesh): void {
        this.registeredMeshes.set(id, mesh);

        // Register with sub-modules
        if (this.hiZOcclusion) {
            this.hiZOcclusion.addOccludee(id, mesh);
            // Large meshes become occluders
            const geometry = mesh.geometry as THREE.BufferGeometry;
            if (geometry) {
                const triangleCount = geometry.index
                    ? geometry.index.count / 3
                    : geometry.getAttribute('position').count / 3;
                if (triangleCount > 1000) {
                    this.hiZOcclusion.addOccluder(mesh);
                }
            }
        }

        if (this.viewDependentLOD) {
            this.viewDependentLOD.registerMesh(id, mesh);
        }

        if (this.edgeDetection) {
            this.edgeDetection.registerMesh(id, mesh);
        }

        if (this.dynamicBatching) {
            this.dynamicBatching.addMesh(mesh);
        }
    }

    /**
     * Unregister a mesh
     */
    unregisterMesh(id: string): void {
        const mesh = this.registeredMeshes.get(id);
        if (!mesh) return;

        this.registeredMeshes.delete(id);

        if (this.hiZOcclusion) {
            this.hiZOcclusion.removeOccludee(id);
            this.hiZOcclusion.removeOccluder(mesh);
        }

        if (this.viewDependentLOD) {
            this.viewDependentLOD.unregisterMesh(id);
        }

        if (this.edgeDetection) {
            this.edgeDetection.unregisterMesh(id);
        }

        if (this.dynamicBatching) {
            this.dynamicBatching.removeMesh(mesh);
        }
    }

    /**
     * Register all meshes in a group/scene
     */
    registerAllMeshes(root: THREE.Object3D, idPrefix: string = ''): void {
        let meshIndex = 0;
        root.traverse((obj) => {
            if (obj instanceof THREE.Mesh) {
                const id = `${idPrefix}mesh_${meshIndex++}`;
                this.registerMesh(id, obj);
            }
        });

        if (this.dynamicBatching && meshIndex > 0) {
            this.dynamicBatching.batchScene();
        }

        if (this.config.debug) {
            console.log(`[FikoPerformance] Registered ${meshIndex} meshes`);
        }
    }

    // ========================================================================
    // UPDATE LOOP
    // ========================================================================

    /**
     * Update all performance modules - call every frame
     */
    update(deltaTime: number): void {
        if (!this.initialized) return;

        const frameStart = performance.now();

        // Update HiZ Occlusion
        if (this.hiZOcclusion && this.registeredMeshes.size > 0) {
            // Build HiZ pyramid periodically (not every frame)
            if (this.frameCount % 2 === 0) {
                this.hiZOcclusion.buildHiZPyramid(this.scene);
            }
        }

        // Update View-Dependent LOD
        if (this.viewDependentLOD) {
            this.viewDependentLOD.update(deltaTime);
        }

        // Update Edge Detection
        if (this.edgeDetection) {
            this.edgeDetection.update();
        }

        // Update Dynamic Batching
        if (this.dynamicBatching) {
            this.dynamicBatching.update();
        }

        // Track frame time
        const frameTime = performance.now() - frameStart;
        this.frameTimeAccum += frameTime;
        this.frameCount++;

        if (this.frameCount % 60 === 0) {
            this.avgFrameTimeMs = this.frameTimeAccum / 60;
            this.frameTimeAccum = 0;
        }
    }

    /**
     * Render edges (call after main scene render)
     */
    renderEdges(): void {
        if (this.edgeDetection) {
            this.edgeDetection.render();
        }
    }

    /**
     * Get visible mesh IDs after occlusion culling
     */
    async getVisibleMeshes(): Promise<string[]> {
        if (!this.hiZOcclusion || this.registeredMeshes.size === 0) {
            return Array.from(this.registeredMeshes.keys());
        }

        const visibility = await this.hiZOcclusion.queryAllOccludees();
        const visible: string[] = [];

        visibility.forEach((isVisible, id) => {
            if (isVisible) {
                visible.push(id);
            }
        });

        return visible;
    }

    // ========================================================================
    // MEMORY POOL ACCESS
    // ========================================================================

    /**
     * Get a pooled Vector3
     */
    acquireVector3(x: number = 0, y: number = 0, z: number = 0): THREE.Vector3 {
        if (this.memoryPoolManager) {
            return this.memoryPoolManager.vector3.acquire(x, y, z);
        }
        return new THREE.Vector3(x, y, z);
    }

    /**
     * Release a pooled Vector3
     */
    releaseVector3(v: THREE.Vector3): void {
        if (this.memoryPoolManager) {
            this.memoryPoolManager.vector3.release(v);
        }
    }

    /**
     * Get a pooled Matrix4
     */
    acquireMatrix4(): THREE.Matrix4 {
        if (this.memoryPoolManager) {
            return this.memoryPoolManager.matrix4.acquire();
        }
        return new THREE.Matrix4();
    }

    /**
     * Release a pooled Matrix4
     */
    releaseMatrix4(m: THREE.Matrix4): void {
        if (this.memoryPoolManager) {
            this.memoryPoolManager.matrix4.release(m);
        }
    }

    /**
     * Get a pooled Box3
     */
    acquireBox3(): THREE.Box3 {
        if (this.memoryPoolManager) {
            return this.memoryPoolManager.box3.acquire();
        }
        return new THREE.Box3();
    }

    /**
     * Release a pooled Box3
     */
    releaseBox3(b: THREE.Box3): void {
        if (this.memoryPoolManager) {
            this.memoryPoolManager.box3.release(b);
        }
    }

    // ========================================================================
    // CLIP PLANES
    // ========================================================================

    /**
     * Add a clip plane for edge detection
     */
    addClipPlane(id: string, plane: THREE.Plane, color?: THREE.Color): void {
        if (this.edgeDetection) {
            this.edgeDetection.addClipPlane(id, plane, color);
        }
    }

    /**
     * Update a clip plane
     */
    updateClipPlane(id: string, plane: THREE.Plane): void {
        if (this.edgeDetection) {
            this.edgeDetection.updateClipPlane(id, plane);
        }
    }

    /**
     * Remove a clip plane
     */
    removeClipPlane(id: string): void {
        if (this.edgeDetection) {
            this.edgeDetection.removeClipPlane(id);
        }
    }

    // ========================================================================
    // STATISTICS
    // ========================================================================

    /**
     * Get comprehensive performance statistics
     */
    getStats(): PerformanceIntegrationStats {
        return {
            initialized: this.initialized,
            initTimeMs: this.initTimeMs,
            frameTimeMs: this.avgFrameTimeMs,
            gpuBackend: this.gpuAdapter?.isWebGPU ? 'webgpu' : (this.gpuAdapter ? 'webgl2' : 'none'),
            sharedMemoryEnabled: this.sharedMemory !== null,
            modules: {
                hiZOcclusion: this.hiZOcclusion?.getStats() || null,
                viewDependentLOD: this.viewDependentLOD?.getStats() || null,
                edgeDetection: this.edgeDetection?.getStats() || null,
                memoryPool: this.memoryPoolManager ? {
                    vector3: this.memoryPoolManager.vector3.getStats(),
                    matrix4: this.memoryPoolManager.matrix4.getStats(),
                    totalMemoryBytes: this.memoryPoolManager.getStats().totalMemoryBytes
                } : null
            }
        };
    }

    /**
     * Log performance summary to console
     */
    logStats(): void {
        const stats = this.getStats();
        console.log('=== FikoPerformance Stats ===');
        console.log(`Initialized: ${stats.initialized} (${stats.initTimeMs.toFixed(2)}ms)`);
        console.log(`GPU Backend: ${stats.gpuBackend}`);
        console.log(`SharedMemory: ${stats.sharedMemoryEnabled}`);
        console.log(`Frame Time: ${stats.frameTimeMs.toFixed(2)}ms`);

        if (stats.modules.hiZOcclusion) {
            console.log(`HiZ: ${stats.modules.hiZOcclusion.objectsCulled} culled, ${stats.modules.hiZOcclusion.visibleObjects} visible`);
        }
        if (stats.modules.viewDependentLOD) {
            console.log(`LOD: ${stats.modules.viewDependentLOD.meshCount} meshes, ${stats.modules.viewDependentLOD.memoryUsedMB.toFixed(2)}MB used`);
        }
        if (stats.modules.edgeDetection) {
            console.log(`Edges: ${stats.modules.edgeDetection.totalEdges} total`);
        }
        if (stats.modules.memoryPool) {
            console.log(`Pool: ${(stats.modules.memoryPool.totalMemoryBytes / 1024).toFixed(2)}KB`);
        }
    }

    // ========================================================================
    // CLEANUP
    // ========================================================================

    /**
     * Dispose all resources
     */
    dispose(): void {
        // Unregister all meshes
        for (const id of this.registeredMeshes.keys()) {
            this.unregisterMesh(id);
        }
        this.registeredMeshes.clear();

        // Dispose modules
        this.dynamicBatching?.dispose();
        this.hiZOcclusion?.dispose();
        this.viewDependentLOD?.dispose();
        this.edgeDetection?.dispose();
        this.memoryPoolManager?.dispose();
        this.gpuAdapter?.dispose();

        this.dynamicBatching = null;
        this.hiZOcclusion = null;
        this.viewDependentLOD = null;
        this.edgeDetection = null;
        this.memoryPoolManager = null;
        this.gpuAdapter = null;
        this.sharedMemory = null;

        this.initialized = false;

        if (this.config.debug) {
            console.log('[FikoPerformance] Disposed');
        }
    }
}

// ============================================================================
// SINGLETON
// ============================================================================

let _integrationInstance: FikoPerformanceIntegration | null = null;

/**
 * Get or create performance integration instance
 */
export function getPerformanceIntegration(
    camera?: THREE.Camera,
    renderer?: THREE.WebGLRenderer,
    scene?: THREE.Scene,
    config?: Partial<PerformanceIntegrationConfig>
): FikoPerformanceIntegration | null {
    if (!_integrationInstance && camera && renderer && scene) {
        _integrationInstance = new FikoPerformanceIntegration(camera, renderer, scene, config);
    }
    return _integrationInstance;
}

/**
 * Dispose singleton instance
 */
export function disposePerformanceIntegration(): void {
    if (_integrationInstance) {
        _integrationInstance.dispose();
        _integrationInstance = null;
    }
}

// ============================================================================
// CONVENIENCE EXPORT
// ============================================================================

export const fikoPerformance = {
    get instance(): FikoPerformanceIntegration | null {
        return _integrationInstance;
    },

    init(
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        scene: THREE.Scene,
        config?: Partial<PerformanceIntegrationConfig>
    ): FikoPerformanceIntegration {
        if (!_integrationInstance) {
            _integrationInstance = new FikoPerformanceIntegration(camera, renderer, scene, config);
        }
        return _integrationInstance;
    },

    dispose(): void {
        disposePerformanceIntegration();
    },

    // Quick access to memory pools
    vec3: (x?: number, y?: number, z?: number) => memoryPool.vec3.acquire(x, y, z),
    mat4: () => memoryPool.mat4.acquire(),
    box3: () => memoryPool.box3.acquire(),

    releaseVec3: (v: THREE.Vector3) => memoryPool.vec3.release(v),
    releaseMat4: (m: THREE.Matrix4) => memoryPool.mat4.release(m),
    releaseBox3: (b: THREE.Box3) => memoryPool.box3.release(b)
};
